import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { ClientesPage } from '@/features/clientes/pages/ClientesPage';
import { ProdutosPage } from '@/features/produtos/pages/ProdutosPage';
import { EstoquePage } from '@/features/estoque/pages/EstoquePage';
import { ColaboradoresPage } from '@/features/colaboradores/pages/ColaboradoresPage';
import { PontoPage } from '@/features/ponto/pages/PontoPage';
import { PosVendaPage } from '@/features/pos-venda/pages/PosVendaPage';
import { usePosVendaEntitlement } from '@/features/pos-venda/hooks/usePosVendaEntitlement';
import { ServicosPage } from '@/features/servicos/pages/ServicosPage';
import { OrcamentosPage } from '@/features/orcamentos/pages/OrcamentosPage';
import { RelatoriosPage } from '@/features/relatorios/pages/RelatoriosPage';
import { FinanceiroPage } from '@/features/financeiro/pages/FinanceiroPage';
import { AcessoPdv, CODIGO_PDV } from '@/features/pdv/components/AcessoPdv';
import { useProdutoEntitlement } from '@/features/complementos/hooks/useProdutoEntitlement';
import { BackupPage } from '@/features/backup/pages/BackupPage';
import { ConfiguracoesPage } from '@/features/configuracoes/pages/ConfiguracoesPage';
import { LimpezaDadosPage } from '@/features/configuracoes/pages/LimpezaDadosPage';
import { BotaoFeedback } from '@/features/backup/pages/BotaoFeedback';
import { useLicenca } from '@/features/licenca/useLicenca';
import { licencaApi, licencaPermiteUso } from '@/features/licenca/licencaApi';
import { AvisoLicenca, BloqueioLicenca } from '@/features/licenca/BloqueioLicenca';
import { MinhaLicencaPage } from '@/features/licenca/MinhaLicencaPage';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/authApi';
import { AuthProvider } from '@/features/auth/AuthContext';
import { EtapasKanbanProvider } from '@/shared/state/EtapasKanbanContext';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { MinhasMaquinasPage } from '@/features/auth/pages/MinhasMaquinasPage';
import { StatusConexao } from '@/features/auth/ui/StatusConexao';
import { IndicadorPosVenda } from '@/features/pos-venda/ui/IndicadorPosVenda';
import { posVendaApi } from '@/features/pos-venda/api/posVendaApi';
import {
  assinarEstadoWhatsapp,
  limparEstadoWhatsapp,
  publicarEstadoInstancia,
} from '@/features/pos-venda/estadoWhatsapp';
import { BannerOffline } from '@/features/auth/ui/BannerOffline';
import { MenuUsuario } from '@/features/auth/ui/MenuUsuario';
import { BotaoTema } from '@/shared/ui/BotaoTema';
import { Toast } from '@/shared/ui/Toast';
import { BannerAtualizacao } from '@/shared/ui/BannerAtualizacao';
import { BannerAviso } from '@/shared/ui/BannerAviso';
import { httpClient } from '@/shared/api/httpClient';
import { plataforma } from '@/platform';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { abaDoCaminho, PREFIXO_ABA, ROTAS, type IdAba } from '@/app/rotas';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import iconeApp from '../assets/icone.png';
import {
  IconBox,
  IconChart,
  IconChevronLeft,
  IconCloud,
  IconCog,
  IconFile,
  IconInbox,
  IconLock,
  IconMessage,
  IconRefresh,
  IconUsers,
  IconWrench,
  IconX,
  IconDollar,
} from '@/shared/ui/Icon';

/** Telas que não estão no menu, mas precisam de título na topbar. */
type Aba = IdAba | 'limpeza' | 'maquinas' | 'licenca' | 'ordemNova' | 'ordemDetalhe';

const itensTodos: Array<{
  id: IdAba;
  label: string;
  Icone: typeof IconUsers;
  /** Quando informado, o item só aparece se a plataforma tiver a capacidade. */
  exigeCapacidade?: keyof typeof plataforma.capacidades;
}> = [
  { id: 'ordens', label: 'Ordens de serviço', Icone: IconFile },
  { id: 'posVenda', label: 'Pós-venda', Icone: IconMessage },
  { id: 'clientes', label: 'Clientes', Icone: IconUsers },
  { id: 'produtos', label: 'Produtos', Icone: IconBox },
  { id: 'estoque', label: 'Estoque', Icone: IconInbox },
  { id: 'colaboradores', label: 'Colaboradores', Icone: IconUsers },
  { id: 'ponto', label: 'Controle de ponto', Icone: IconChart },
  { id: 'servicos', label: 'Serviços', Icone: IconWrench },
  { id: 'pdv', label: 'PDV', Icone: IconDollar },
  { id: 'financeiro', label: 'Financeiro', Icone: IconChart },
  { id: 'relatorios', label: 'Relatórios', Icone: IconChart },
  // No web os dados já vivem no servidor — não há arquivo local pra
  // enviar nem restaurar, então a aba não aparece.
  { id: 'backup', label: 'Backup', Icone: IconCloud, exigeCapacidade: 'backupLocal' },
  { id: 'configuracoes', label: 'Configurações', Icone: IconCog },
];

const itens = itensTodos.filter(
  (item) => !item.exigeCapacidade || plataforma.capacidades[item.exigeCapacidade],
);

const titulos: Record<Aba, string> = {
  ordens: 'Ordens de serviço',
  ordemNova: 'Nova ordem de serviço',
  ordemDetalhe: 'Ordem de serviço',
  posVenda: 'Pós-venda por WhatsApp',
  clientes: 'Cadastro de Clientes',
  produtos: 'Catálogo de Produtos',
  estoque: 'Estoque',
  colaboradores: 'Colaboradores',
  ponto: 'Controle de ponto',
  servicos: 'Catálogo de Serviços',
  pdv: 'Venda rápida',
  financeiro: 'Financeiro',
  relatorios: 'Relatórios',
  backup: 'Backup na nuvem',
  configuracoes: 'Configurações',
  limpeza: 'Limpar dados',
  maquinas: 'Minhas máquinas',
  licenca: 'Minha assinatura',
};

function IconMenu() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2" y="5" width="16" height="1.5" rx="0.75" fill="currentColor" />
      <rect x="2" y="9.25" width="16" height="1.5" rx="0.75" fill="currentColor" />
      <rect x="2" y="13.5" width="16" height="1.5" rx="0.75" fill="currentColor" />
    </svg>
  );
}

// Tela cheia de carregamento usada no boot e durante a verificação de
// licença pós-login — evita expor o app (ou a tela de bloqueio) antes da
// decisão estar tomada.
function TelaSplash({ texto }: { texto: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        color: '#94a3b8',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
      }}
    >
      {texto}
    </div>
  );
}

/**
 * Botão de ícone ao lado da versão: dispara a verificação manual de
 * atualização do app. Mostra o ícone girando enquanto checa e um título
 * com o resultado. Download/instalação ficam a cargo do BannerAtualizacao.
 */
function BotaoAtualizar() {
  const [estado, setEstado] = useState<'idle' | 'checando' | 'atualizado' | 'erro'>(
    'idle',
  );

  useEffect(() => {
    const off = plataforma.updater.aoEvento((ev) => {
      if (ev.tipo === 'checando') setEstado('checando');
      else if (ev.tipo === 'nao-disponivel') setEstado('atualizado');
      else if (ev.tipo === 'erro') setEstado('erro');
      // disponivel/baixando/pronto: o BannerAtualizacao assume o feedback.
      else setEstado('idle');
    });
    return off;
  }, []);

  // Volta ao estado neutro depois de exibir o resultado por alguns segundos.
  useEffect(() => {
    if (estado !== 'atualizado' && estado !== 'erro') return;
    const t = window.setTimeout(() => setEstado('idle'), 4000);
    return () => window.clearTimeout(t);
  }, [estado]);

  async function checar() {
    setEstado('checando');
    try {
      await plataforma.updater.checarAgora();
    } catch {
      setEstado('erro');
    }
  }

  const titulo =
    estado === 'checando'
      ? 'Verificando atualizações…'
      : estado === 'atualizado'
        ? 'Você já está na versão mais recente'
        : estado === 'erro'
          ? 'Falha ao verificar atualização'
          : 'Verificar atualizações';

  return (
    <button
      type="button"
      className="sidebar__atualizar"
      onClick={() => void checar()}
      disabled={estado === 'checando'}
      aria-label={titulo}
      data-tooltip-id="sidebar-tip"
      data-tooltip-content={titulo}
    >
      <IconRefresh
        width={16}
        height={16}
        className={estado === 'checando' ? 'girando' : undefined}
      />
    </button>
  );
}

export function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // A URL é a fonte da verdade da navegação: o menu só reflete o
  // caminho atual, e voltar/avançar do navegador funcionam de graça.
  const aba: Aba = abaDoCaminho(pathname) ?? 'ordens';
  const [sidebarAberta, setSidebarAberta] = useState(false);
  // Modo compacto da sidebar (só ícones). Persistido entre sessões.
  const [sidebarColapsada, setSidebarColapsada] = useState(
    () => localStorage.getItem('orcamento.sidebar.colapsada') === '1',
  );
  useEffect(() => {
    localStorage.setItem('orcamento.sidebar.colapsada', sidebarColapsada ? '1' : '0');
  }, [sidebarColapsada]);
  const [, setStatusApi] = useState<'verificando' | 'ok' | 'falha'>('verificando');
  const {
    estado: licenca,
    carregando: licencaCarregando,
    erro: licencaErro,
    recheck,
  } = useLicenca();
  const {
    estado: authEstado,
    carregando: authCarregando,
    revalidando: authRevalidando,
    recarregar: recarregarAuth,
    revalidarAgora,
  } = useAuth();
  const dialogos = useDialogos();
  const {
    ativo: posVendaAtivo,
    entitlement: posVendaEntitlement,
    carregando: posVendaCarregando,
    erro: posVendaErro,
    recarregar: recarregarPosVenda,
  } = usePosVendaEntitlement();

  // Revalida o pós-venda no servidor ao abrir a aba (pega ativação recente
  // feita no admin) e periodicamente enquanto logado (pega um bloqueio para
  // recolocar o cadeado e travar a tela na hora).
  useEffect(() => {
    if (aba === 'posVenda') void recarregarPosVenda(true);
  }, [aba, recarregarPosVenda]);
  useEffect(() => {
    if (!authEstado?.logado) return;
    const t = window.setInterval(() => void recarregarPosVenda(true), 120_000);
    return () => window.clearInterval(t);
  }, [authEstado?.logado, recarregarPosVenda]);

  // PDV também é produto add-on. Mesma revalidação do pós-venda, com uma
  // diferença: no desktop ele funciona offline com a cópia assinada do
  // direito de uso, então sem conexão a checagem não é forçada — forçar só
  // trocaria essa cópia por "sem conexão" e trancaria o balcão à toa.
  const acessoPdv = useProdutoEntitlement(CODIGO_PDV);
  const { recarregar: recarregarPdv } = acessoPdv;
  const forcarPdv = authEstado?.status === 'online';
  useEffect(() => {
    if (aba === 'pdv') void recarregarPdv(forcarPdv);
    // `forcarPdv` fica de fora de propósito: só a entrada na aba revalida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba, recarregarPdv]);
  useEffect(() => {
    if (!authEstado?.logado) return;
    // O mount do hook roda antes do login (sem sessão); revalida assim que
    // a sessão existe, para o cadeado do menu não esperar 2 minutos.
    void recarregarPdv();
    const t = window.setInterval(() => void recarregarPdv(forcarPdv), 120_000);
    return () => window.clearInterval(t);
  }, [authEstado?.logado, recarregarPdv, forcarPdv]);

  /**
   * O PDV ocupa a tela inteira: quem está no balcão não deve ver doze
   * opções de menu nem precisar do mouse. O item na barra lateral é só a
   * porta — a saída fica dentro da própria tela. Sem o produto, a tela de
   * bloqueio aparece no layout normal (com o menu), como no pós-venda.
   * Durante a primeira verificação já entra em tela cheia: é o caso comum
   * de quem tem o PDV e recarregou a página no balcão.
   */
  const modoQuiosque =
    aba === 'pdv' && (acessoPdv.ativo || acessoPdv.verificandoPrimeiraVez);
  /** Cadeado no menu só quando já se sabe que está travado — sem piscar. */
  const abaTrancada: Partial<Record<IdAba, boolean>> = {
    posVenda: !posVendaAtivo,
    pdv: !acessoPdv.ativo && !acessoPdv.verificandoPrimeiraVez,
  };

  // Status do WhatsApp para o indicador do topo. Só consulta com o produto
  // ativo — cada chamada bate no servidor, e sem assinatura não há o que
  // mostrar. Intervalo folgado (60s): é um indicador, não um monitor.
  const [whatsappConectado, setWhatsappConectado] = useState<boolean | null>(null);
  const [whatsappNumero, setWhatsappNumero] = useState<string | null>(null);
  useEffect(() => {
    if (!authEstado?.logado || !posVendaAtivo) {
      setWhatsappConectado(null);
      setWhatsappNumero(null);
      limparEstadoWhatsapp();
      return;
    }
    let cancelado = false;
    const aplicar = (estado: { status: string; numero: string | null }) => {
      if (cancelado) return;
      setWhatsappConectado(estado.status === 'conectada');
      setWhatsappNumero(estado.numero);
      // Alinha a tela de conexão do pós-venda, que tem o próprio polling.
      publicarEstadoInstancia(estado);
    };
    const consultar = async () => {
      try {
        aplicar(await posVendaApi.statusWhatsapp());
      } catch {
        // Offline ou servidor fora: mantém o último estado conhecido em vez
        // de piscar "desconectado" a cada falha de rede.
      }
    };
    void consultar();
    const t = window.setInterval(() => void consultar(), 60_000);
    // O servidor empurra a mudança quando a uazapi avisa (webhook
    // `connection`) — o ícone acompanha a conexão sem esperar o intervalo.
    const off = plataforma.aoMudarPosVenda(aplicar);
    // A tela de conexão descobre a leitura do QR pelo polling curto dela;
    // sem isto o ícone da topbar continuaria riscado até o próximo minuto.
    const offLocal = assinarEstadoWhatsapp((e) => {
      if (cancelado) return;
      setWhatsappConectado(e.conectado);
      setWhatsappNumero(e.numero);
    });
    return () => {
      cancelado = true;
      window.clearInterval(t);
      off();
      offLocal();
    };
  }, [authEstado?.logado, posVendaAtivo, aba]);

  // Detecta transição online ⇄ offline para mostrar toast informativo.
  const [statusAnterior, setStatusAnterior] = useState<string | null>(null);
  const [toastConexao, setToastConexao] = useState<{
    tipo: 'sucesso' | 'aviso';
    mensagem: string;
  } | null>(null);
  useEffect(() => {
    if (!authEstado?.logado) return;
    const atual = authEstado.status;
    if (statusAnterior && statusAnterior !== atual) {
      if (atual === 'online') {
        setToastConexao({ tipo: 'sucesso', mensagem: 'Conexão restaurada.' });
      } else if (statusAnterior === 'online') {
        setToastConexao({
          tipo: 'aviso',
          // No web os dados moram no servidor: prometer "salvo localmente"
          // seria falso, e o usuário perderia o que digitasse confiando nisso.
          mensagem: plataforma.capacidades.offline
            ? 'Você ficou sem conexão. Trabalho continua salvo localmente; backup será retomado quando voltar.'
            : 'Sem conexão com o servidor. O que você fizer agora não será salvo — aguarde a reconexão.',
        });
      }
    }
    setStatusAnterior(atual);
  }, [authEstado?.status, authEstado?.logado, statusAnterior]);

  useEffect(() => {
    httpClient
      .get<{ status: string }>('/health')
      .then((r) => setStatusApi(r.status === 'ok' ? 'ok' : 'falha'))
      .catch(() => setStatusApi('falha'));
  }, []);

  // Reconciliação pós-login. Dispara uma vez quando authEstado.logado
  // vira true (login ou signup). Pega pagamentos PIX/cartão feitos na
  // landing pública antes da instalação e aplica nesta máquina.
  // Best-effort: falhas só vão para o console, não interrompem o uso.
  const [reconciliouUsuarioId, setReconciliouUsuarioId] = useState<string | null>(null);
  // Marca que a primeira verificação de licença pós-login ainda está em
  // andamento — enquanto isso mostramos o splash, não a tela de bloqueio.
  const [verificandoPosLogin, setVerificandoPosLogin] = useState(false);
  useEffect(() => {
    if (!authEstado?.logado || !authEstado.conta) return;
    const usuarioId = authEstado.conta.usuarioId;
    if (reconciliouUsuarioId === usuarioId) return;
    setReconciliouUsuarioId(usuarioId);
    setVerificandoPosLogin(true);
    licencaApi
      .reconciliar()
      .catch((err) => console.warn('[licenca] reconciliação falhou:', err))
      .finally(() => {
        // SEMPRE revalida a licença agora que a sessão está persistida. O
        // recheck do mount do useLicenca roda antes do login (sem token →
        // 'sem-acesso'); sem este recheck pós-login, a tela de bloqueio
        // apareceria indevidamente até o usuário clicar em "Verificar".
        void recheck().finally(() => setVerificandoPosLogin(false));
      });
  }, [authEstado?.logado, authEstado?.conta, reconciliouUsuarioId, recheck]);

  async function exportarDados() {
    try {
      await httpClient.post('/api/backup/run', {});
      await dialogos.alertar({
        titulo: 'Backup enviado',
        mensagem:
          'Backup do seu banco enviado para o servidor com sucesso.\n' +
          'Você pode acessar este backup pelo painel administrativo ou solicitar ao suporte.',
      });
    } catch (err) {
      await dialogos.alertar({
        titulo: 'Falha no backup',
        mensagem:
          'Não foi possível enviar o backup agora.\n' +
          (err instanceof Error ? err.message : 'Erro desconhecido') +
          '\nO arquivo do banco está em "%APPDATA%/orcamento-desktop/" e pode ser copiado manualmente.',
      });
    }
  }

  const bloqueado = licenca && !licencaPermiteUso(licenca);

  function navegarPara(destino: string) {
    navigate(destino);
    setSidebarAberta(false);
  }

  // Splash enquanto licença / auth ainda não foram verificadas — evita expor
  // o app por uma fração de segundo antes da decisão.
  if ((licencaCarregando && !licenca) || (authCarregando && !authEstado)) {
    return <TelaSplash texto="Inicializando…" />;
  }

  // Gate de auth: se não está logado, mostra a tela de login.
  // Importante: a tela de login NÃO depende da licença — em primeira instalação
  // o usuário ainda não tem assinatura, precisa criar conta para receber trial.
  if (authEstado && !authEstado.logado) {
    return <LoginPage estado={authEstado} onAutenticado={recarregarAuth} />;
  }

  // Logo após o login, a licença pode ainda refletir o estado pré-login
  // ('sem-acesso', do recheck do mount sem token). Enquanto a verificação
  // pós-login roda, mostramos o splash em vez da tela de bloqueio — só
  // bloqueamos se, depois de revalidar, o servidor confirmar o problema.
  if (verificandoPosLogin && bloqueado) {
    return <TelaSplash texto="Verificando licença…" />;
  }

  // Quando bloqueado por licença, NÃO renderizamos a UI por trás. Mesmo que o
  // usuário remova a modal via DevTools, não há app montado por baixo — e o IPC
  // do processo principal recusa qualquer chamada não-essencial enquanto não
  // permitido. Defesa em profundidade: visual + back-end.
  if (bloqueado && licenca) {
    return (
      <BloqueioLicenca
        estado={licenca}
        recheck={recheck}
        carregando={licencaCarregando}
        erro={licencaErro}
        exportarDados={exportarDados}
        onSair={async () => {
          try {
            await authApi.logout();
          } catch {
            // sessão local é limpa mesmo se o servidor falhar
          }
          await recarregarAuth();
        }}
      />
    );
  }

  return (
    <AuthProvider estado={authEstado}>
    <EtapasKanbanProvider>
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {licenca && <AvisoLicenca estado={licenca} />}
      <BannerOffline estado={authEstado} />
      <BannerAtualizacao />
      <BannerAviso />
      <Toast
        mensagem={toastConexao?.mensagem ?? null}
        tipo={toastConexao?.tipo}
        onFechar={() => setToastConexao(null)}
      />
      <div
        className={`app-shell${sidebarColapsada ? ' app-shell--sidebar-colapsada' : ''}${
          modoQuiosque ? ' app-shell--quiosque' : ''
        }`}
        style={{ flex: 1, minHeight: 0 }}
      >
      {/* Overlay mobile */}
      <div
        className={`sidebar__overlay${sidebarAberta ? ' sidebar__overlay--visivel' : ''}`}
        onClick={() => setSidebarAberta(false)}
        aria-hidden="true"
      />

      <aside
        className={`sidebar${sidebarAberta ? ' sidebar--aberta' : ''}${
          sidebarColapsada ? ' sidebar--colapsada' : ''
        }`}
      >
        <div className="sidebar__brand">
          <img className="sidebar__brand-mark" src={iconeApp} alt="" aria-hidden="true" />
          <div className="sidebar__brand-text">
            <div className="sidebar__brand-title">Oficina</div>
            <div className="sidebar__brand-subtitle">Ágil</div>
          </div>
          {/* Retrair o menu só faz sentido onde ele fica fixo ao lado do
              conteúdo. No celular ele é uma gaveta sobreposta: o que se
              espera ali é fechar, não encolher. */}
          <button
            type="button"
            className="sidebar__toggle"
            onClick={() => setSidebarColapsada((v) => !v)}
            aria-label={sidebarColapsada ? 'Expandir menu' : 'Retrair menu'}
            data-tooltip-id="sidebar-tip"
            data-tooltip-content={sidebarColapsada ? 'Expandir menu' : 'Retrair menu'}
            aria-pressed={sidebarColapsada}
          >
            <IconChevronLeft className="sidebar__toggle-icon" width={18} height={18} />
          </button>
          <button
            type="button"
            className="sidebar__fechar"
            onClick={() => setSidebarAberta(false)}
            aria-label="Fechar menu"
          >
            <IconX width={18} height={18} />
          </button>
        </div>

        <div>
          <div className="sidebar__section-label">Operação</div>
          <nav className="sidebar__nav">
            {itens.map(({ id, label, Icone }) => (
              <Link
                key={id}
                to={PREFIXO_ABA[id]}
                className="sidebar__item"
                onClick={() => setSidebarAberta(false)}
                aria-current={aba === id ? 'page' : undefined}
                data-tooltip-id="sidebar-tip"
                data-tooltip-content={sidebarColapsada ? label : undefined}
              >
                <Icone className="sidebar__item-icon" />
                <span className="sidebar__item-label">{label}</span>
                {abaTrancada[id] && (
                  <>
                    <IconLock
                      width={14}
                      height={14}
                      style={{ marginLeft: 'auto', opacity: 0.7 }}
                      aria-hidden="true"
                    />
                    <span className="sr-only"> (pacote à parte, não liberado)</span>
                  </>
                )}
              </Link>
            ))}
          </nav>
        </div>

        <div className="sidebar__footer">
          <div className="sidebar__versao">
            <span className="sidebar__versao-texto">
              <span className="sidebar__versao-num">v{plataforma.versao}</span>
              <span className="sidebar__versao-edicao">
                {plataforma.capacidades.alvo === 'web' ? ' · Web' : ' · Desktop Edition'}
              </span>
            </span>
            {plataforma.capacidades.autoUpdate && <BotaoAtualizar />}
          </div>
          <BotaoFeedback contexto={`aba:${aba}`} colapsado={sidebarColapsada} />
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="topbar__hamburger"
              aria-label="Abrir menu"
              data-tooltip-id="icon-tip"
              onClick={() => setSidebarAberta((v) => !v)}
            >
              <IconMenu />
            </button>
            <div className="topbar__title">{titulos[aba]}</div>
          </div>
          <div className="topbar__user" style={{ gap: 12 }}>
            <BotaoTema />
            {authEstado && posVendaAtivo && (
              <IndicadorPosVenda
                conectado={whatsappConectado}
                numero={whatsappNumero}
                onAbrir={() => navegarPara(ROTAS.posVenda)}
              />
            )}
            {authEstado && (
              <StatusConexao
                estado={authEstado}
                onAtualizar={revalidarAgora}
                atualizando={authRevalidando}
              />
            )}
            {authEstado && (
              <MenuUsuario
                estado={authEstado}
                onAbrirMaquinas={() => navegarPara(ROTAS.maquinas)}
                onAbrirLicenca={() => navegarPara(ROTAS.licenca)}
                onLogout={recarregarAuth}
              />
            )}
          </div>
        </div>

        {/*
          Cada tela é uma rota. As sub-rotas de criação, edição e
          visualização são resolvidas dentro da própria página — ela lê
          os parâmetros da URL e abre o formulário correspondente, de modo
          que um link para `/clientes/novo` já chega com o modal aberto.
        */}
        <Routes>
          <Route path="/" element={<Navigate to={ROTAS.ordens} replace />} />

          <Route path="/ordens-servico/*" element={<OrcamentosPage />} />

          <Route
            path="/pos-venda"
            element={
              <PosVendaPage
                ativo={posVendaAtivo}
                carregando={posVendaCarregando}
                erro={posVendaErro}
                entitlement={posVendaEntitlement}
                recarregar={recarregarPosVenda}
              />
            }
          />

          <Route path="/clientes/*" element={<ClientesPage />} />
          <Route path="/produtos/*" element={<ProdutosPage />} />
          <Route path="/estoque/*" element={<EstoquePage />} />
          <Route path="/colaboradores/*" element={<ColaboradoresPage />} />
          <Route path="/ponto/*" element={<PontoPage />} />
          <Route path="/servicos/*" element={<ServicosPage />} />
          {/* Sem o produto, nenhuma tela do PDV é montada: o gate mostra a
              tela de bloqueio em qualquer sub-rota (/pdv/caixa, /pdv/vendas…). */}
          <Route path="/pdv/*" element={<AcessoPdv acesso={acessoPdv} />} />
          <Route path="/financeiro/*" element={<FinanceiroPage />} />
          <Route path="/relatorios/*" element={<RelatoriosPage />} />

          {plataforma.capacidades.backupLocal && (
            <Route path="/backup" element={<BackupPage />} />
          )}

          <Route
            path="/configuracoes"
            element={<ConfiguracoesPage onAbrirLimpeza={() => navegarPara(ROTAS.limpeza)} />}
          />
          <Route
            path="/configuracoes/limpar-dados"
            element={<LimpezaDadosPage onVoltar={() => navegarPara(ROTAS.configuracoes)} />}
          />

          <Route
            path="/conta/maquinas"
            element={
              <MinhasMaquinasPage maquinaAtualId={authEstado?.conta?.maquinaId ?? null} />
            }
          />
          <Route
            path="/conta/assinatura"
            element={
              <MinhaLicencaPage
                estado={licenca}
                recheck={recheck}
                carregando={licencaCarregando}
              />
            }
          />

          {/* Endereço desconhecido volta para a tela inicial em vez de
              deixar a área de conteúdo em branco. */}
          <Route path="*" element={<Navigate to={ROTAS.ordens} replace />} />
        </Routes>
      </main>
      </div>
      <Tooltip id="sidebar-tip" place="right" className="app-tooltip" />
      {/* Tooltip global para botões só de ícone. Liga-se a qualquer
          `.btn--icon` e a botões avulsos via data-tooltip-id="icon-tip";
          o conteúdo vem do data-tooltip-content, se houver (texto mais rico
          que o rótulo, ou num envoltório de botão desabilitado), senão do
          aria-label (ou title) do próprio botão. O seletor lista os dois
          casos: com `anchorSelect` o react-tooltip ignora o data-tooltip-id. */}
      <Tooltip
        id="icon-tip"
        anchorSelect=".btn--icon:not([data-tooltip-id]), [data-tooltip-id='icon-tip']"
        place="top"
        className="app-tooltip"
        render={({ content, activeAnchor }) =>
          content ||
          activeAnchor?.getAttribute('aria-label') ||
          activeAnchor?.getAttribute('title') ||
          null
        }
      />
    </div>
    </EtapasKanbanProvider>
    </AuthProvider>
  );
}
